import { createFileRoute } from "@tanstack/react-router";
import { CategoryLanding, categoryHead, productsQueryOptions } from "@/components/CategoryLanding";

// Dedicated landing for "taaperotorni" (90/mo). Synonymic with oppimistorni
// but Google needs the exact keyword in <title>/<h1> to rank top-10.
// /oppimistorni still owns "oppimistorni" (1 600/mo) + "seisontajakkara".
const SLUG = "taaperotorni";
const H1 = "Taaperotorni — turvallinen oppimistorni 1–5-vuotiaalle";
const TITLE = "Taaperotorni lapselle 1–5 v — turvallinen keittiöjakkara | Montessori24";
const DESC =
  "Taaperotorni eli oppimistorni: kaiteilla suojattu keittiöjakkara, jolla taapero yltää työtasolle. Koivuvaneri, säädettävä askelma. Ilmainen toimitus 3–7 pv.";

const FAQS = [
  {
    q: "Mikä on taaperotorni?",
    a: "Taaperotorni on 1–5-vuotiaalle mitoitettu apujakkara, jossa on suojaavat kaiteet neljältä sivulta. Lapsi nousee tornin päälle ja yltää keittiön työtasolle turvallisesti — voi pestä porkkanoita, sekoittaa taikinaa ja osallistua ruoanlaittoon vanhemman rinnalla. Sama laite tunnetaan myös nimillä oppimistorni ja learning tower.",
  },
  {
    q: "Onko taaperotorni sama asia kuin oppimistorni?",
    a: "Kyllä. ‘Taaperotorni’ ja ‘oppimistorni’ ovat kaksi eri nimeä samasta tuotteesta. Taaperotorni-sana painottaa käyttäjää (taapero eli 1–3-vuotias), oppimistorni Montessori-pedagogiikkaa. Englanniksi molemmat ovat ‘learning tower’ tai ‘kitchen helper tower’.",
  },
  {
    q: "Minkä ikäiselle taaperotorni sopii?",
    a: "Käyttöikä alkaa yleensä 12 kk iässä, kun lapsi seisoo tukevasti itsenäisesti. Säädettävän askelmakorkeuden ansiosta sama torni palvelee 5-vuotiaaksi asti. 1–3-vuotias hyötyy eniten kaiteista; isompi lapsi käyttää tornia jo lähes apujakkarana.",
  },
  {
    q: "Onko taaperotorni turvallinen aivan pienelle?",
    a: "On, kun torni on vakaa (leveä alusta), askelmakorkeus säädetty lapselle sopivaksi ja käyttö on valvottua. Tarkista tuotekohtainen ikäsuositus ja turvallisuustiedot tuotesivulta. Suositus: alle 2-vuotiaan kanssa aikuinen aina käden ulottuvilla.",
  },
  {
    q: "Mitä eroa on taaperotornilla ja tavallisella apujakkaralla?",
    a: "Tavallisessa apujakkarassa ei ole kaiteita, joten se sopii vasta 4–5-vuotiaalle. Taaperotorni on jakkara + neljä kaidetta: 1–3-vuotias voi seistä turvallisesti työtason korkeudella eikä putoa vaikka horjahtaisi. Käyttöikä on 3–5× pidempi kuin perinteisellä apujakkaralla.",
  },
];

export const Route = createFileRoute("/taaperotorni")({
  loader: ({ context }) => context.queryClient.ensureQueryData(productsQueryOptions),
  head: () => categoryHead({ slug: SLUG, title: TITLE, h1: H1, description: DESC, faqs: FAQS }),
  component: TaaperotorniPage,
});

function TaaperotorniPage() {
  return (
    <CategoryLanding
      slug={SLUG}
      eyebrow="Taaperotorni"
      title={H1}
      intro="Taaperotorni nostaa 1–5-vuotiaan keittiön työtasolle turvallisesti. Neljä suojaavaa kaidetta, leveä jalusta, säädettävä askelma — sama torni palvelee taaperovuodesta esikouluikään. Tunnetaan myös nimillä oppimistorni ja learning tower."
      kauppaCategory="tower"
      match={(t) => /torni|apujakkar|jakkar|helper tower|kitchen tower|tower|stool/i.test(t)}
      relatedChips={[
        { label: "Oppimistorni", to: "/oppimistorni" },
        { label: "Oppimistorni-opas", to: "/oppimistorni-opas" },
        { label: "Turvallisuusopas", to: "/montessori-koulu/oppimistorni-turvallisuus" },
        { label: "Lapsi keittiössä (ikäopas)", to: "/montessori-koulu/lapsi-keittiossa-ikaopas" },
        { label: "Kaikki tornit", to: "/kauppa" },
      ]}
      body={[
        {
          heading: "Miksi taaperotorni on ykköshankinta keittiöön",
          paragraphs: [
            "Taapero haluaa tehdä kaiken itse — ja Montessori-pedagogiikka sanoo että hänen kannattaa antaa. Ongelma on mittakaava: keittiön työtaso on 90 cm korkealla, 1-vuotias 75 cm. Taaperotorni ratkaisee tämän nostamalla lapsen turvallisesti aikuisen työkorkeudelle, jotta hän voi osallistua leipomiseen, tiskaamiseen tai vain katsomiseen kun ruoka valmistuu.",
            "Karkeamotoriikka, hienomotoriikka, keskittyminen ja itsetunto kehittyvät samaan aikaan. Tavallinen tuoli tai apujakkara ei ole vaihtoehto alle 3-vuotiaalle: horjahdus lattialle 45 cm korkeudesta voi tarkoittaa aivotärähdystä. Taaperotorni suojaa neljältä sivulta, joten aikuinen voi keskittyä ruoanlaittoon.",
          ],
        },
        {
          heading: "Taaperotorni vai oppimistorni — kummalla nimellä etsiä?",
          paragraphs: [
            "Sama tuote. Suomalaisissa vanhempain-ryhmissä puhutaan sekä taaperotornista että oppimistornista. Kaupallisesti oppimistorni on yleisempi nimitys (englannin learning tower), mutta taaperotorni kuvaa käyttäjää paremmin: taapero eli 12 kk–3-vuotias, jolle kaiteet ovat pakolliset.",
            "Meidän valikoimastamme löydät sekä perinteisen taaperotornin (kiinteä korkeus) että säädettävän oppimistornin, jossa askelmaa siirretään lapsen kasvun mukaan. Molemmat toimivat myös liitutauluna tai 2-in-1-tuolina + pöytänä käyttöiän pidentämiseksi.",
          ],
        },
        {
          heading: "Mihin kiinnittää huomiota taaperotornia ostaessa",
          paragraphs: [
            "Vakaus: leveä jalusta ja matala painopiste. Kokeile kaupassa horjuttaa — kunnollinen taaperotorni pysyy paikallaan vaikka lapsi nojaa sivulle.",
            "Kaiteiden korkeus: taaperon lantion yläpuolelle. Liian matalat kaiteet eivät estä sivulle putoamista.",
            "Etuseinän aukon leveys: lapsen kokoinen, ei liian väljä. Aivan pieni voisi luiskahtaa läpi jos aukko on aikuiselle mitoitettu.",
            "Materiaali ja pintakäsittely: koivuvaneri ja vesiohenteinen, lapsiturvallinen lakka. Lapsi puree ja nuolee — pinnan on kestettävä myös suutesti.",
            "Säädettävä askelma: pidentää käyttöikää 1 vuodesta 5 vuoteen. Ilman säätöä joudut vaihtamaan tornin lapsen kasvaessa.",
          ],
        },
      ]}
      faqs={FAQS}
      cta={{
        heading: "Valmis tuomaan taaperon keittiöön turvallisesti?",
        body: "Tuotekohtaiset mitat, materiaalit ja ikäsuositus löydät jokaisen tornin omalta sivulta. Ilmainen toimitus 3–7 päivässä ja 30 päivän palautusetu.",
        primaryLabel: "Tutustu taaperotorni-mallistoon",
        secondaryLabel: "Lue oppimistorni-opas",
        secondaryTo: "/oppimistorni-opas",
      }}
    />
  );
}